import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Stepper, Step, StepLabel, Button, Typography, TextField,
  Paper, IconButton, Select, MenuItem, FormControl, InputLabel, FormControlLabel,
  Switch, Chip, Grid, Card, CardContent, Divider, Alert, CircularProgress,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { v4 as uuidv4 } from 'uuid';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const FIELD_TYPES = [
  { value: 'text',     label: 'Text' },
  { value: 'number',   label: 'Number' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'select',   label: 'Dropdown' },
  { value: 'rating',   label: 'Rating (1–5)' },
  { value: 'date',     label: 'Date' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'imageUrl', label: 'Image URL' },
];

const STEPS = ['Basic Info', 'Fields', 'Layout', 'Publish'];

function newField() {
  return { id: uuidv4(), label: '', type: 'text', required: false, options: [], gridProps: { xs: 12, sm: 6 }, order: 0 };
}

function SortableFieldRow({ field, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, p: 1.5, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2 }}>
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', mt: 1, color: 'text.secondary' }}>
        <DragHandleIcon />
      </Box>
      <Grid container spacing={1} sx={{ flex: 1 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            label="Field Label" size="small" fullWidth required
            value={field.label}
            onChange={e => onChange({ ...field, label: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type" value={field.type}
              onChange={e => onChange({ ...field, type: e.target.value, options: [] })}
            >
              {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControlLabel
            label="Req."
            control={<Switch size="small" checked={field.required} onChange={e => onChange({ ...field, required: e.target.checked })} />}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <ToggleButtonGroup
            size="small" exclusive
            value={field.gridProps?.sm === 12 ? 'full' : 'half'}
            onChange={(_, v) => { if (v) onChange({ ...field, gridProps: { xs: 12, sm: v === 'full' ? 12 : 6 } }); }}
          >
            <ToggleButton value="half">½</ToggleButton>
            <ToggleButton value="full">Full</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        {field.type === 'select' && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Options (comma-separated)</Typography>
            <TextField
              size="small" fullWidth
              placeholder="e.g. Red, Blue, Green"
              value={field.options.join(', ')}
              onChange={e => onChange({ ...field, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            />
          </Grid>
        )}
      </Grid>
      <IconButton size="small" onClick={onDelete} sx={{ mt: 0.5, color: 'error.main' }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function MockCard({ schema, fields }) {
  const subtitleValues = (schema.subtitleFields || [])
    .map(id => fields.find(f => f.id === id)?.label)
    .filter(Boolean).join(' · ');
  const detailValues = (schema.detailFields || [])
    .map(id => fields.find(f => f.id === id)?.label)
    .filter(Boolean).join(' · ');
  return (
    <Card sx={{ maxWidth: 260, background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h5">{schema.icon || '📦'}</Typography>
          <Typography variant="subtitle1" fontWeight={700}>{schema.name || 'Collection Name'}</Typography>
        </Box>
        {subtitleValues && <Typography variant="caption" color="text.secondary" display="block">{subtitleValues}</Typography>}
        {detailValues && <Typography variant="body2" sx={{ mt: 0.5 }}>{detailValues}</Typography>}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>★ ★ ★ ☆ ☆</Typography>
      </CardContent>
    </Card>
  );
}

export default function SchemaDesigner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const isEdit = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');

  // Step 2
  const [fields, setFields] = useState([]);

  // Step 3
  const [subtitleFields, setSubtitleFields] = useState([]);
  const [detailFields, setDetailFields] = useState([]);
  const [filterField, setFilterField] = useState('');

  // Step 4
  const [isPublished, setIsPublished] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (isEdit) {
      axiosInstance.get(`/custom-schemas/${id}`)
        .then(r => {
          const s = r.data;
          setName(s.name || '');
          setDescription(s.description || '');
          setIcon(s.icon || '');
          setFields(s.fields || []);
          setSubtitleFields(s.subtitleFields || []);
          setDetailFields(s.detailFields || []);
          setFilterField(s.filterField || '');
          setIsPublished(s.isPublished || false);
        })
        .catch(() => setError('Failed to load schema'));
    }
  }, [id, isEdit]);

  if (!featureFlags?.enableCustomCollections) {
    return (
      <div>
        <AppBarHeader hideCollectionNav />
        <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">Custom Collections is not enabled.</Typography>
        </Container>
      </div>
    );
  }

  const handleFieldChange = (updated) => {
    setFields(prev => prev.map(f => f.id === updated.id ? updated : f));
  };
  const handleAddField = () => setFields(prev => [...prev, { ...newField(), order: prev.length }]);
  const handleDeleteField = (fieldId) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    setSubtitleFields(prev => prev.filter(id => id !== fieldId));
    setDetailFields(prev => prev.filter(id => id !== fieldId));
    if (filterField === fieldId) setFilterField('');
  };
  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      setFields(prev => {
        const oldIndex = prev.findIndex(f => f.id === active.id);
        const newIndex = prev.findIndex(f => f.id === over.id);
        return arrayMove(prev, oldIndex, newIndex).map((f, i) => ({ ...f, order: i }));
      });
    }
  };

  const handleSave = async (publish = false) => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), description, icon, fields, subtitleFields, detailFields, filterField };
      let schema;
      if (isEdit) {
        schema = (await axiosInstance.put(`/custom-schemas/${id}`, payload)).data;
      } else {
        schema = (await axiosInstance.post('/custom-schemas', payload)).data;
      }
      if (publish && !schema.isPublished) {
        await axiosInstance.post(`/custom-schemas/${schema._id}/publish`);
      }
      navigate(`/custom/${schema._id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canNext = () => {
    if (activeStep === 0) return name.trim().length > 0;
    if (activeStep === 1) return fields.every(f => f.label.trim());
    return true;
  };

  return (
    <div>
      <AppBarHeader hideCollectionNav />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          {isEdit ? 'Edit Collection Schema' : 'Create Collection Schema'}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 0 — Basic Info */}
        {activeStep === 0 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Basic Info</Typography>
            <TextField label="Collection Name" required fullWidth value={name} onChange={e => setName(e.target.value)} sx={{ mb: 2 }} />
            <TextField label="Description" fullWidth multiline rows={2} value={description} onChange={e => setDescription(e.target.value)} sx={{ mb: 2 }} />
            <TextField label="Icon (emoji)" fullWidth value={icon} onChange={e => setIcon(e.target.value)} placeholder="e.g. 🎸" inputProps={{ maxLength: 4 }} />
          </Paper>
        )}

        {/* Step 1 — Fields */}
        {activeStep === 1 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Fields</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define the fields users will fill in for each item. Drag to reorder.
            </Typography>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                {fields.map(f => (
                  <SortableFieldRow
                    key={f.id} field={f}
                    onChange={handleFieldChange}
                    onDelete={() => handleDeleteField(f.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Button startIcon={<AddIcon />} onClick={handleAddField} sx={{ mt: 1 }}>Add Field</Button>
          </Paper>
        )}

        {/* Step 2 — Layout */}
        {activeStep === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Layout</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose which fields to show on collection cards.
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Subtitle Fields</InputLabel>
                  <Select
                    multiple label="Subtitle Fields" value={subtitleFields}
                    onChange={e => setSubtitleFields(e.target.value)}
                    renderValue={selected => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(id => <Chip key={id} size="small" label={fields.find(f => f.id === id)?.label || id} />)}
                      </Box>
                    )}
                  >
                    {fields.filter(f => f.label).map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Detail Fields</InputLabel>
                  <Select
                    multiple label="Detail Fields" value={detailFields}
                    onChange={e => setDetailFields(e.target.value)}
                    renderValue={selected => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(id => <Chip key={id} size="small" label={fields.find(f => f.id === id)?.label || id} />)}
                      </Box>
                    )}
                  >
                    {fields.filter(f => f.label).map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Filter Field</InputLabel>
                  <Select label="Filter Field" value={filterField} onChange={e => setFilterField(e.target.value)}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {fields.filter(f => f.label && ['text','select'].includes(f.type)).map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">Preview</Typography>
              <MockCard schema={{ name, icon, subtitleFields, detailFields }} fields={fields} />
            </Grid>
          </Grid>
        )}

        {/* Step 3 — Publish */}
        {activeStep === 3 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Share to Library</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Publishing makes your schema visible in the community library so others can adopt it.
              You can unpublish at any time — users who already adopted it will keep their data.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Summary</Typography>
              <Typography variant="body2">Name: <strong>{name}</strong></Typography>
              <Typography variant="body2">Fields: <strong>{fields.length}</strong></Typography>
              <Typography variant="body2">Icon: <strong>{icon || '—'}</strong></Typography>
            </Box>
            <FormControlLabel
              label="Publish to community library"
              control={<Switch checked={isPublished} onChange={e => setIsPublished(e.target.checked)} color="primary" />}
            />
          </Paper>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep(s => s - 1)}>Back</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep < STEPS.length - 1 ? (
              <Button variant="contained" disabled={!canNext()} onClick={() => setActiveStep(s => s + 1)}>Next</Button>
            ) : (
              <Button variant="contained" disabled={saving || !name.trim()} onClick={() => handleSave(isPublished)}>
                {saving ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Schema'}
              </Button>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
}
